import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, Label } from '@c2fo/liquidity';
import FilterDrawer from '@/enterprise/components/FilterDrawer';
import RangePicker from '@/enterprise/components/RangePicker';
import { dateRanges, getRangeLabel, type DateRange } from '@/enterprise/features/history/utils';
import { SubstitutionRangeKey } from './substitutionRecordsFilters';

interface SubstitutionRecordsFilterProps {
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  rangeKey: SubstitutionRangeKey;
  startDate?: string;
  endDate?: string;
  /** Called with `null` for "All time". */
  onDateRangeChange: (bag: { range: DateRange; rangeKey: Exclude<SubstitutionRangeKey, 'allTime'> } | null) => void;
}

/**
 * Substitution Date filter. There is no customer filter: platform's stats route can't filter by
 * customer or division, so it would have to be built client-side from data the route doesn't own.
 */
const SubstitutionRecordsFilter = ({
  filterOpen,
  setFilterOpen,
  rangeKey,
  startDate,
  endDate,
  onDateRangeChange,
}: SubstitutionRecordsFilterProps) => {
  const { t } = useTranslation();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);

  const dateOptions = [
    { key: 'allTime' as const, label: t('instantPay.substitutions.allTime') },
    ...Object.keys(dateRanges).map((key) => ({
      key: key as keyof typeof dateRanges,
      label: t(...getRangeLabel(key)),
    })),
  ];

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        <div className="w-full space-y-1">
          <Label>{t('instantPay.substitutions.substitutionDate')}</Label>
          <div className="rounded border border-stroke bg-white p-4">
            <ul className="space-y-2">
              {dateOptions.map(({ key, label }) => (
                <li key={key}>
                  <label htmlFor={`substitution-date-${key}`} className="flex cursor-pointer items-center gap-2">
                    <input
                      id={`substitution-date-${key}`}
                      className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                      checked={rangeKey === key}
                      onChange={() =>
                        onDateRangeChange(key === 'allTime' ? null : { range: dateRanges[key], rangeKey: key })
                      }
                      type="radio"
                    />
                    {label}
                  </label>
                </li>
              ))}
              <li>
                <RangePicker
                  className={cn({ 'font-bold': rangeKey === 'custom' })}
                  // A cleared custom range has no dates left, so fall back to the page default of All time.
                  onChange={(range) => onDateRangeChange(range ? { range, rangeKey: 'custom' } : null)}
                  onOpenChange={setCustomPickerOpen}
                  open={customPickerOpen}
                  value={
                    startDate && endDate
                      ? { startDate, endDate }
                      : { startDate: dateRanges.last30Days.startDate, endDate: dateRanges.last30Days.endDate }
                  }
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default SubstitutionRecordsFilter;
